import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeader from "@/components/SectionHeader";
import EditorialMarquee from "@/components/Marquee";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/data/content";

const HIGHLIGHTS = [
  { title: "Quality-Focused Manufacturing", desc: "Every product is made with attention to materials, construction and finish." },
  { title: "Wide Product Range", desc: "Bedding, bath textiles, room essentials and hospitality linen under one roof." },
  { title: "Hospitality Industry Focus", desc: "Our range is planned around the real needs of hotels, resorts and institutions." },
  { title: "Consistent Workmanship", desc: "Careful stitching, finishing and presentation across every order." },
  { title: "Professional Service", desc: "Clear communication and a structured approach to every enquiry." },
  { title: "B2B Supply Capability", desc: "Set up to serve bulk, wholesale and institutional requirements." },
  { title: "Reliable Customer Support", desc: "A responsive team that stays reachable before and after supply." },
];

const About = () => (
  <main data-testid="about-page">
    <PageHero
      eyebrow="About Us"
      title="Hotel Linen Manufacturer &"
      italic="Hospitality Textile Supplier"
      description="Amazing Grace is a hospitality textile and hotel-products manufacturer based in Karamadai, Coimbatore, Tamil Nadu — serving hotels, resorts and institutions across India, including Tamil Nadu, Kerala and Karnataka."
      image={IMG.fabric}
    />

    <section className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <Reveal className="relative order-2 lg:order-1">
          <div className="absolute -bottom-4 -left-4 w-full h-full border border-gold/50 pointer-events-none" />
          <div className="overflow-hidden group">
            <img
              src={IMG.hotelRoom2}
              alt="Premium hotel bedding by Amazing Grace"
              className="w-full h-[420px] md:h-[540px] object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.04]"
            />
          </div>
        </Reveal>
        <div className="order-1 lg:order-2">
          <SectionHeader number="01" eyebrow="Who We Are" title="About Amazing Grace" />
          <Reveal delay={0.15}>
            <p className="mt-6 text-navy/70 text-base md:text-lg font-light leading-relaxed">
              Amazing Grace manufactures and supplies hospitality textiles and hotel essentials from Coimbatore, Tamil Nadu. Our work focuses on the practical needs of hospitality and institutional buyers — products that look refined, feel comfortable and stand up to everyday use.
            </p>
            <p className="mt-5 text-navy/70 text-base md:text-lg font-light leading-relaxed">
              From bed linen and duvets to towels, bathrobes and table napkins, we support hotels, resorts, hospitals, guest houses, hostels, service apartments, restaurants, distributors and corporate buyers with dependable manufacturing and professional service.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    <EditorialMarquee />

    <section className="py-24 md:py-32 bg-linen/40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <SectionHeader
          number="02"
          eyebrow="What Defines Us"
          title="The Way We Work"
          description="Seven commitments that shape every product we manufacture and every relationship we build."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
          {HIGHLIGHTS.map((h, i) => (
            <Reveal key={h.title} delay={i * 0.06} className={i === 0 ? "lg:row-span-1" : ""}>
              <div
                data-testid={`highlight-${h.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group bg-ivory border border-navy/10 p-9 h-full transition-colors duration-500 hover:border-gold/60"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-gold/50">
                  <Check size={15} className="text-gold" />
                </span>
                <h3 className="font-serif text-xl md:text-2xl font-medium text-navy mt-6">{h.title}</h3>
                <p className="text-sm text-navy/60 font-light mt-3 leading-relaxed">{h.desc}</p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.4}>
            <Link
              to="/quote"
              data-testid="about-cta-card"
              className="group flex flex-col justify-between bg-navy p-9 h-full min-h-[220px] transition-colors duration-500 hover:bg-ink"
            >
              <span className="font-serif italic text-2xl text-gold">Work with us</span>
              <span className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-ivory mt-8">
                Request a Quote
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <SectionHeader
            number="03"
            eyebrow="Our Home"
            title="Made in Coimbatore, a City of Textiles"
            description="Coimbatore has been a centre of textile craft and manufacturing for generations. Amazing Grace carries that heritage forward — pairing regional textile knowledge with the standards modern hospitality businesses expect."
          />
        </div>
        <Reveal delay={0.15} className="relative">
          <div className="absolute -top-4 -right-4 w-full h-full border border-gold/50 pointer-events-none" />
          <div className="overflow-hidden group">
            <img
              src={IMG.fabric}
              alt="Textile fabric close-up"
              className="w-full h-[380px] md:h-[480px] object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.04]"
            />
          </div>
        </Reveal>
      </div>
    </section>

    <CTASection
      title="Let's Build Better Hospitality Experiences Together."
      text="Connect with Amazing Grace for quality hospitality textiles and hotel essentials."
      image={IMG.resort}
    />
  </main>
);

export default About;
